import { Component, Input, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import {
  catchError,
  delay,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { uuid } from 'uuid';
import * as _ from 'lodash-es';

import { UserGroupAttributes } from '~app/models/user-group';
import { PersonAttributes, PersonAttributesSlim } from '~app/models/person';
import { UserGroupService } from '~app/services/user-group.service';
import { RolePermissionService } from '~app/services/role-permission.service';
import { PersonService } from '~app/services/person.service';
import { BooleanInput } from '@angular/cdk/coercion';
import { FormsUtility } from '~app/utilities/';
import { CiaoSharedModule } from '~app/components/shared/shared.module';
import { CiaoFormsModule } from '~app/components/shared/forms/forms.module';
import { NgFor, NgIf } from '@angular/common';
import { TeamMemberRowComponent } from '../team-member-row/team-member-row.component';

const supervisorErrorMessage =
  'This is required. Choose at least one supervisor who is not also a team member';
const teammemberErrorMessage =
  'This is required. Choose at least one team member who is not also a supervisor';
const supervisorErrorSet = { atLeastOne: supervisorErrorMessage };
const teammemberErrorSet = { atLeastOne: teammemberErrorMessage };
const oneRolePerTeamMessage =
  'User already has a role. Remove it before assigning a new one.';

@Component({
    imports: [CiaoSharedModule, CiaoFormsModule, NgIf, NgFor, TeamMemberRowComponent],
    selector: 'ciao-user-group-form',
    templateUrl: './user-group-form.component.html',
    styleUrls: ['./user-group-form.component.less']
})
export class UserGroupFormComponent implements OnInit {
  @Input() isEditForm: BooleanInput = false;
  @Input() canEditUserGroup = false;
  // Whether the user can change which Region/Forest this team belongs to.
  // Tied to `create_child_usergroup` (i.e., admin scope). Supervisors have
  // neither this nor `edit_usergroup`, so for them the entire team-details
  // section (name, region, forest, POC) is read-only — they manage members
  // only. Admins edit details via `edit_usergroup_nested` over their scope.
  @Input() canEditParentGroup = false;
  @Input() canEditTeamMembers = false;
  @Input() canEditSupervisors = false;
  @Input() canEditDispatchers = false;
  @Input() canEditGuests = false;
  @Input() canViewTeamMembers = false;
  @Input() set userGroupId(id: string | null | undefined) {
    // `null` is load-bearing: it signals Add mode (distinct from `undefined`, which means "unset").
    if (id === undefined) return;
    this._userGroupId = id;
    if (this._initialized) {
      this.refreshUserGroup(id);
    } else {
      this._pendingRefresh = true;
    }
  }
  _userGroupId: string | null = null;
  private _initialized = false;
  private _pendingRefresh = false;

  regionStation: UserGroupAttributes;
  forestLab: UserGroupAttributes;

  private currentUserGroupSubscription: Subscription;
  private readonly subscriptions: Subscription = new Subscription();

  get dataNew(): Partial<FormData> {
    return this.userGroupFormGroup.value;
  }

  get allowNavigate(): boolean {
    return !this.userGroupFormGroup.dirty;
  }

  get allowSave(): boolean {
    // When the user can't edit the user group (e.g. Dispatcher editing only
    // their team's dispatcher list), saveFormData skips saving userGroupAttributes
    // and only dispatches role changes — so userGroupFormGroup validity is moot.
    // pointOfContactPersonControl is checked in the editable case because
    // it's the v2.4 authoritative gate for POC selection.
    const userGroupValid =
      !this.canEditUserGroup ||
      (this.userGroupFormGroup.valid &&
        this.pointOfContactPersonControl.valid);
    return userGroupValid && this.membersFormGroup.valid;
  }
  userGroupControls = {
    id: new FormControl(''),
    label: new FormControl('', [
      Validators.required,
      FormsUtility.isOverCharLimit,
    ]),
    tier: new FormControl(3, {
      validators: [Validators.required],
      nonNullable: true,
    }),
    tierLabel: new FormControl('Team', { nonNullable: true }),
    description: new FormControl(''),
    // v2.4: pointOfContact and contactEmail are derived from
    // pointOfContactPersonControl (below) — they're kept on the form group so
    // the existing API contract is preserved on save. Required validation
    // moves to the new control; we keep email-shape validation on
    // contactEmail as a safety net.
    pointOfContact: new FormControl('', [FormsUtility.isOverCharLimit]),
    contactEmail: new FormControl('', [Validators.email]),
    parentId: new FormControl(''),
    forestLab: new FormControl('', Validators.required),
    regionStation: new FormControl('', Validators.required),
    parent: new FormControl<UserGroupAttributes>(null),
  };
  membersControls = {
    nextTeamMember: new FormControl<PersonAttributes>(null),
    nextSupervisor: new FormControl<PersonAttributes>(null),
    nextDispatcher: new FormControl<PersonAttributes>(null),
    nextGuest: new FormControl<PersonAttributes>(null),
    listTeamMember: new FormControl<PersonAttributes[]>([], {
      validators: Validators.required,
      nonNullable: true,
    }),
    listSupervisor: new FormControl<PersonAttributes[]>([], {
      validators: Validators.required,
      nonNullable: true,
    }),
    listDispatcher: new FormControl<PersonAttributes[]>([], {
      nonNullable: true,
    }),
    listGuest: new FormControl<PersonAttributes[]>([], {
      nonNullable: true,
    }),
  };
  userGroupFormGroup = new FormGroup(this.userGroupControls);
  membersFormGroup = new FormGroup(this.membersControls);

  /**
   * v2.4 POC: a usda-only person picker. Kept outside the form group on
   * purpose — refreshUserGroup's setValue() requires every form-group key
   * to be present in the loaded data, and synthesizing this from the API's
   * (pointOfContact, contactEmail) strings happens *after* the load. The
   * control's value is the authoritative source for whether POC is filled
   * in; on every emission we patch the form's pointOfContact + contactEmail
   * so the existing save path keeps working unchanged.
   *
   * Error keys this control can carry:
   *   - required:    no POC selected
   *   - pocNotFound: the loaded contactEmail didn't match any USDA user in
   *                  PersonService.allPeople. Stays until the operator picks
   *                  a valid POC or clears the team's contactEmail.
   */
  pointOfContactPersonControl = new FormControl<PersonAttributesSlim | null>(
    null,
    Validators.required
  );

  /**
   * Match the selected POC to a dropdown option by person id. The combobox's
   * default compareWith is reference equality, which fails after
   * `hydratePocPhone` replaces the control value with a spread copy
   * (`{...current, cellPhone}`) — the new object no longer === any option, so
   * the field falls back to its placeholder instead of showing the chosen
   * person. Comparing by id keeps the selected POC displayed.
   */
  comparePocById = (
    a: PersonAttributesSlim | null,
    b: PersonAttributesSlim | null
  ): boolean => !!a && !!b && a.id === b.id;

  /**
   * v2.4: true when the team's saved contactEmail didn't match any USDA user
   * in the directory on modal open. Surfaces the "This contact is no longer
   * found..." block in the template. Cleared as soon as the operator picks a
   * valid POC.
   *
   * Tracked as plain state instead of a form-control error so it doesn't get
   * auto-rendered as "Pocnotfound: True" by the FieldComponent's built-in
   * error list — we want a dedicated, longer-form message under the field.
   * Save is still gated by the standard `required` validator (the control is
   * left null on orphan), so this flag is purely for UX messaging.
   */
  pocOrphaned = false;

  initialSupervisors: PersonAttributes[] = [];
  initialTeamMembers: PersonAttributes[] = [];
  initialDispatchers: PersonAttributes[] = [];
  initialGuests: PersonAttributes[] = [];

  readonly defaultData: UserGroupAttributes = {
    id: '',
    label: '',
    tier: 3,
    tierLabel: 'Team',
    description: '',
    pointOfContact: '',
    contactEmail: '',
    parentId: '',
    regionStation: null,
    forestLab: null,
    parent: null,
  };

  readonly allRegionsOptions$: Observable<{ label: string; value: uuid }[]>;
  readonly allForestLabOptions$: Observable<{ label: string; value: uuid }[]>;

  constructor(
    private userGroupService: UserGroupService,
    private rolePermissionService: RolePermissionService,
    private personService: PersonService,
    private cdr: ChangeDetectorRef,
  ) {
    const { regionOptions$, forestOptions$ } =
      this.getParentGroupDropdownOptions();
    this.allRegionsOptions$ = regionOptions$;
    this.allForestLabOptions$ = forestOptions$;
  }

  ngOnInit(): void {
    // The "at least one" rules only apply at team creation. Legacy teams may
    // have already lost their last supervisor or team member; we shouldn't
    // block other edits (e.g. updating the dispatcher list) on data the user
    // didn't intend to change and may not have permission to fix.
    this.membersControls.listSupervisor.addValidators((control) => {
      if (this.isEditForm) {
        this.membersControls.nextSupervisor.setErrors(null);
        return null;
      }
      let errors = null;
      if (control.value?.length < 1) {
        errors = supervisorErrorSet;
      }
      this.membersControls.nextSupervisor.setErrors(errors);
      return errors;
    });
    this.membersControls.listTeamMember.addValidators((control) => {
      if (this.isEditForm) {
        this.membersControls.nextTeamMember.setErrors(null);
        return null;
      }
      let errors = null;
      if (control.value?.length < 1) {
        errors = teammemberErrorSet;
      }
      this.membersControls.nextTeamMember.setErrors(errors);
      return errors;
    });
    // Supervisors and Team Members are USDA-only per v2.4. The dropdown is
    // already filtered to USDA users via subtype="people-usda", but mirror the
    // Dispatcher/Guest pattern with a runtime reject so the path is safe
    // against future regressions or any other code that pokes nextSupervisor
    // / nextTeamMember directly.
    let s1 = this.membersControls.nextSupervisor.valueChanges
      .pipe(
        // NOTE: intentionally no distinctUntilChanged here. The combobox
        // re-fires the same person on focusout (~100ms later), which re-runs
        // the control's native validators and wipes any error we set below.
        // Re-processing the same value lets us re-apply the conflict / USDA
        // error so it persists instead of flashing and disappearing.
        filter((value) => !!value),
        tap((person) => {
          if (!this.isUsdaPerson(person)) {
            this.membersControls.nextSupervisor.setErrors({
              notUsda: 'Supervisors/admins must be USDA.gov users',
            });
            this.membersControls.nextSupervisor.markAsTouched();
            return;
          }
          const conflict = this.conflictingRoleLabel(
            person.id,
            this.membersControls.listSupervisor
          );
          if (conflict) {
            this.membersControls.nextSupervisor.setErrors({
              oneRolePerTeam: oneRolePerTeamMessage,
            });
            this.membersControls.nextSupervisor.markAsTouched();
            return;
          }
          this.membersControls.nextSupervisor.setErrors(null);
          let arr = this.membersControls.listSupervisor
            .value as PersonAttributes[];
          arr.push(person);
          arr = arr.filter(
            (value, index, self) =>
              self.findIndex((person) => person.id === value.id) === index
          );
          this.membersControls.listSupervisor.setValue(arr);
        }),
        delay(0),
        tap(() => {
          if (!this.membersControls.nextSupervisor.errors) {
            this.membersControls.nextSupervisor.setValue(null);
          }
        })
      )
      .subscribe();
    let s2 = this.membersControls.nextTeamMember.valueChanges
      .pipe(
        // NOTE: intentionally no distinctUntilChanged here. The combobox
        // re-fires the same person on focusout (~100ms later), which re-runs
        // the control's native validators and wipes any error we set below.
        // Re-processing the same value lets us re-apply the conflict / USDA
        // error so it persists instead of flashing and disappearing.
        filter((value) => !!value),
        tap((person) => {
          if (!this.isUsdaPerson(person)) {
            this.membersControls.nextTeamMember.setErrors({
              notUsda: 'Team members must be USDA.gov users',
            });
            this.membersControls.nextTeamMember.markAsTouched();
            return;
          }
          const conflict = this.conflictingRoleLabel(
            person.id,
            this.membersControls.listTeamMember
          );
          if (conflict) {
            this.membersControls.nextTeamMember.setErrors({
              oneRolePerTeam: oneRolePerTeamMessage,
            });
            this.membersControls.nextTeamMember.markAsTouched();
            return;
          }
          this.membersControls.nextTeamMember.setErrors(null);
          let arr = this.membersControls.listTeamMember
            .value as PersonAttributes[];
          arr.push(person);
          arr = arr.filter(
            (value, index, self) =>
              self.findIndex((person) => person.id === value.id) === index
          );
          this.membersControls.listTeamMember.setValue(arr);
        }),
        delay(0),
        tap(() => {
          if (!this.membersControls.nextTeamMember.errors) {
            this.membersControls.nextTeamMember.setValue(null);
          }
        })
      )
      .subscribe();

    let s3 = this.membersControls.nextDispatcher.valueChanges
      .pipe(
        // NOTE: intentionally no distinctUntilChanged here. The combobox
        // re-fires the same person on focusout (~100ms later), which re-runs
        // the control's native validators and wipes any error we set below.
        // Re-processing the same value lets us re-apply the conflict / USDA
        // error so it persists instead of flashing and disappearing.
        filter((value) => !!value),
        tap((person) => {
          if (this.isUsdaPerson(person)) {
            this.membersControls.nextDispatcher.setErrors({
              notNonUsda: 'Dispatchers can only be non-USDA.gov users',
            });
            this.membersControls.nextDispatcher.markAsTouched();
            return;
          }
          const conflict = this.conflictingRoleLabel(
            person.id,
            this.membersControls.listDispatcher
          );
          if (conflict) {
            this.membersControls.nextDispatcher.setErrors({
              oneRolePerTeam: oneRolePerTeamMessage,
            });
            this.membersControls.nextDispatcher.markAsTouched();
            return;
          }
          this.membersControls.nextDispatcher.setErrors(null);
          let arr = this.membersControls.listDispatcher
            .value as PersonAttributes[];
          arr.push(person);
          arr = arr.filter(
            (value, index, self) =>
              self.findIndex((person) => person.id === value.id) === index
          );
          this.membersControls.listDispatcher.setValue(arr);
        }),
        delay(0),
        tap(() => {
          if (!this.membersControls.nextDispatcher.errors) {
            this.membersControls.nextDispatcher.setValue(null);
          }
        })
      )
      .subscribe();

    // v2.4 Guests: mirror the nextDispatcher pattern — reject USDA users
    // (Guests are non-USDA-only) and otherwise append to listGuest, de-duped
    // by id. Failure to validate stays on `nextGuest.errors` and keeps the
    // input in an error state until the operator clears it.
    let s5 = this.membersControls.nextGuest.valueChanges
      .pipe(
        // NOTE: intentionally no distinctUntilChanged here. The combobox
        // re-fires the same person on focusout (~100ms later), which re-runs
        // the control's native validators and wipes any error we set below.
        // Re-processing the same value lets us re-apply the conflict / USDA
        // error so it persists instead of flashing and disappearing.
        filter((value) => !!value),
        tap((person) => {
          if (this.isUsdaPerson(person)) {
            this.membersControls.nextGuest.setErrors({
              notNonUsda: 'Guests can only be non-USDA.gov users',
            });
            this.membersControls.nextGuest.markAsTouched();
            return;
          }
          const conflict = this.conflictingRoleLabel(
            person.id,
            this.membersControls.listGuest
          );
          if (conflict) {
            this.membersControls.nextGuest.setErrors({
              oneRolePerTeam: oneRolePerTeamMessage,
            });
            this.membersControls.nextGuest.markAsTouched();
            return;
          }
          this.membersControls.nextGuest.setErrors(null);
          let arr = this.membersControls.listGuest
            .value as PersonAttributes[];
          arr.push(person);
          arr = arr.filter(
            (value, index, self) =>
              self.findIndex((p) => p.id === value.id) === index
          );
          this.membersControls.listGuest.setValue(arr);
        }),
        delay(0),
        tap(() => {
          if (!this.membersControls.nextGuest.errors) {
            this.membersControls.nextGuest.setValue(null);
          }
        })
      )
      .subscribe();

    // v2.4 POC sync: when the POC person changes, mirror their displayName
    // and email into the form's pointOfContact + contactEmail so the existing
    // save path (which posts those two strings) doesn't need to change.
    // emitEvent:false on patchValue prevents the form group's valueChanges
    // from cascading and re-triggering downstream subscriptions.
    //
    // The initial load path uses setValue(..., { emitEvent: false }) on the
    // POC control, so this subscription only fires on user-driven changes
    // (picking a POC in the combobox, or tapping Remove on the mobile
    // person-card). Both should mark the form dirty — without that, the
    // "Unsaved Changes" indicator and any future navigation guards miss
    // POC-only edits because patchValue doesn't propagate dirty state and
    // the POC control isn't a member of userGroupFormGroup.
    let s4 = this.pointOfContactPersonControl.valueChanges
      .pipe(
        tap((person) => {
          const name = person?.displayName ?? '';
          const email = this.extractEmailFromDisplayName(
            person?.displayNameDropdown ?? ''
          );
          this.userGroupFormGroup.patchValue(
            { pointOfContact: name, contactEmail: email ?? '' },
            { emitEvent: false }
          );
          this.userGroupFormGroup.markAsDirty();
          // Any user-driven POC change — picking a new person or removing
          // the current one via the mobile card's Remove — resolves the
          // "saved email isn't in the directory" state. Without clearing
          // on null, the banner lingers after Remove.
          this.pocOrphaned = false;
          this.hydratePocPhone(person?.id ?? null);
        })
      )
      .subscribe();

    this.subscriptions.add(s1);
    this.subscriptions.add(s2);
    this.subscriptions.add(s3);
    this.subscriptions.add(s4);
    this.subscriptions.add(s5);

    this._initialized = true;
    if (this._pendingRefresh) {
      this._pendingRefresh = false;
      this.refreshUserGroup(this._userGroupId);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.currentUserGroupSubscription?.unsubscribe();
  }

  refreshUserGroup(userGroupId: uuid) {
    this.userGroupFormGroup.reset();
    this.membersFormGroup.reset();
    this.pointOfContactPersonControl.reset();
    this._userGroupId = userGroupId;
    let obs: Observable<
      [
        UserGroupAttributes,
        { [roleId: string]: PersonAttributes[] },
        PersonAttributesSlim[]
      ]
    >;
    if (userGroupId) {
      let userGroup$ = this.userGroupService.findById(userGroupId);
      let getMembers$ = this.userGroupService.getMembersByRole(userGroupId, [
        'team_member',
        'supervisor',
        'dispatcher',
        'guest',
      ]);
      // allPeople feeds the v2.4 POC pre-fill: we look the team's stored
      // contactEmail up against the directory so we can either set the POC
      // person control or mark it as orphaned (pocNotFound).
      // `_allPeople` is a BehaviorSubject seeded with `[]`, so a bare
      // `take(1)` would grab the seed on slow loads and mark every team
      // with a saved POC as orphaned. Skip the empty emission so we wait
      // until the directory has actually loaded.
      let allPeople$ = this.personService.allPeople.pipe(
        filter((people) => people.length > 0),
        take(1)
      );
      obs = combineLatest([userGroup$, getMembers$, allPeople$]);
    } else {
      obs = of([this.defaultData, {}, []]);
    }

    obs
      .pipe(
        take(1),
        delay(100),
        tap(([userGroup, memberLists, allPeople]) => {
          let userGroupForm = _.mergeWith(
            {},
            this.defaultData,
            this.convertObjectsToData(userGroup),
            (a, b) => (b === null ? a : undefined)
          );
          this.userGroupFormGroup.setValue(userGroupForm);
          this.userGroupFormGroup.markAsPristine();
          this.userGroupFormGroup.markAsUntouched();
          this.initialSupervisors = memberLists['supervisor'] ?? [];
          this.initialTeamMembers = memberLists['team_member'] ?? [];
          this.initialDispatchers = memberLists['dispatcher'] ?? [];
          this.initialGuests = memberLists['guest'] ?? [];
          // v2.4 migration: keep `initial*` as the loaded DB state (so the
          // diff in GetRoleDifferences persists any cross-section moves on
          // save) and patch the form with the post-migration shape.
          const migrated = this.applyV24Migration({
            supervisors: this.initialSupervisors,
            teamMembers: this.initialTeamMembers,
            dispatchers: this.initialDispatchers,
            guests: this.initialGuests,
          });
          this.membersFormGroup.patchValue({
            listSupervisor: migrated.supervisors.slice(),
            listTeamMember: migrated.teamMembers.slice(),
            listDispatcher: migrated.dispatchers.slice(),
            listGuest: migrated.guests.slice(),
          });
          this.resolvePocFromContactEmail(userGroup?.contactEmail, allPeople);
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  /**
   * Match the team's saved contactEmail against the directory and set the
   * v2.4 POC person control accordingly.
   *  - Match found → setValue (emitEvent:false so the sync subscription
   *    doesn't re-patch identical values onto the form).
   *  - No match + non-empty saved email → leave the control empty and stamp
   *    `pocNotFound` so the modal renders the orphaned-contact error state.
   *  - No saved email (e.g. Add Team mode) → leave the control empty; the
   *    standard `required` error will surface once the user touches the form.
   */
  private resolvePocFromContactEmail(
    savedEmail: string | null | undefined,
    allPeople: PersonAttributesSlim[]
  ): void {
    const normalized = savedEmail?.toLowerCase()?.trim() ?? '';
    if (!normalized) {
      // No saved POC — fresh team, or contactEmail was cleared by an admin.
      // The required validator handles this; no extra error.
      this.pointOfContactPersonControl.setValue(null, { emitEvent: false });
      return;
    }
    const match = allPeople.find(
      (person) =>
        this.extractEmailFromDisplayName(person.displayNameDropdown) ===
        normalized
    );
    if (match) {
      this.pointOfContactPersonControl.setValue(match, { emitEvent: false });
      this.pocOrphaned = false;
      this.hydratePocPhone(match.id);
    } else {
      this.pointOfContactPersonControl.setValue(null, { emitEvent: false });
      this.pocOrphaned = true;
      this.hydratePocPhone(null);
    }
  }

  /**
   * Counter to disambiguate concurrent findById responses: every call to
   * `hydratePocPhone` bumps this, and a returning fetch only writes if its
   * captured value still matches. Without this, switching POCs quickly
   * could leave the previous person's phone visible on the new one.
   */
  private pocPhoneRequestId = 0;

  /**
   * Pull the POC's phone number on demand via /api/people/:id and snapshot
   * it onto the slim person held by `pointOfContactPersonControl`. We keep
   * the bulk dropdown endpoint phone-less because that endpoint is reachable
   * by pre-registration sessions; per-id fetches go through the registered
   * /people gates instead. `findById` already merges the response into
   * `_allPeople`, so once a POC's phone is hydrated it sticks for the rest
   * of the session.
   *
   * Prefers the person's default-flagged phone (`defaultPhone`, the
   * NotificationNumber they marked as default) and falls back to the
   * deprecated `cellPhone` column only for legacy rows with no flagged
   * default. Failures are intentionally silent — phone is decorative, not a
   * save blocker, and the template falls back to "No phone on file".
   */
  private hydratePocPhone(personId: string | null): void {
    const requestId = ++this.pocPhoneRequestId;
    if (!personId) return;
    this.personService
      .findById(personId)
      .pipe(take(1))
      .subscribe({
        next: (full) => {
          if (requestId !== this.pocPhoneRequestId) return;
          const current = this.pointOfContactPersonControl.value;
          if (current?.id !== personId) return;
          const phone =
            full?.defaultPhone?.phoneNumber ?? full?.cellPhone ?? undefined;
          this.pointOfContactPersonControl.setValue(
            { ...current, cellPhone: phone },
            { emitEvent: false }
          );
          this.cdr.markForCheck();
        },
        error: () => {
          // Decorative field — keep "No phone on file" rather than surfacing.
        },
      });
  }

  /**
   * displayNameDropdown is formatted "First Last (email@usda.gov)". Returns
   * the email portion lowercased, or null if no parenthesized email was found.
   * Public so the template can call it for the POC chip's Email row without
   * needing a dedicated pipe.
   */
  extractEmailFromDisplayName(
    displayNameDropdown: string | null | undefined
  ): string | null {
    if (!displayNameDropdown) return null;
    const m = displayNameDropdown.match(/\(([^()]+)\)\s*$/);
    return m ? m[1].trim().toLowerCase() : null;
  }

  /**
   * Whether a person is a USDA user. The migration runs against the result
   * of `getMembersByRole`, which doesn't include the `users` association on
   * the response (so the precomputed `isUSDAUser` virtual is also missing) —
   * but `displayNameDropdown` is always present and always contains the
   * person's USDA email when they have one. Parsing the email out of that
   * label is the most reliable signal available client-side here.
   *
   * Falls back to the `users` array for callers (Add Dispatcher / Add Guest
   * dropdowns) that have it.
   */
  private isUsdaPerson(person: PersonAttributes | PersonAttributesSlim): boolean {
    if (typeof person.isUSDAUser === 'boolean') return person.isUSDAUser;
    const emailFromLabel = this.extractEmailFromDisplayName(
      person.displayNameDropdown
    );
    if (emailFromLabel) return emailFromLabel.endsWith('@usda.gov');
    const full = person as PersonAttributes;
    return !!full.users?.some((u) =>
      u?.email?.toLowerCase().endsWith('@usda.gov')
    );
  }

  /**
   * v2.4 modal-open migration (spec §8): the team-edit modal must re-shape
   * legacy role assignments so what the user sees in each section matches
   * the new "usda-only / non-usda-only" rules.
   *
   *  - Dispatchers must be non-USDA. A USDA dispatcher who is also a
   *    Supervisor is simply pulled out of Dispatchers (kept as Supervisor).
   *    Otherwise they're moved to Team Members.
   *  - Team Members must be USDA. A non-USDA Team Member is moved to Guests
   *    unless they're already in (post-migration) Dispatchers — in which
   *    case they stay there only.
   *  - Supervisors are left alone (no spec rule for migrating non-USDA
   *    Supervisors, and the role is USDA-only by convention).
   *  - Guests already on the team carry through.
   *
   * Migration is UI-only — the `initial*` arrays still reflect the loaded
   * database state, so on Update the diff in GetRoleDifferences will
   * generate the right assign/unassign calls to persist the move. If the
   * operator clicks Cancel, nothing changes server-side and the next
   * open re-applies the same migration (idempotent).
   */
  private applyV24Migration(input: {
    supervisors: PersonAttributes[];
    teamMembers: PersonAttributes[];
    dispatchers: PersonAttributes[];
    guests: PersonAttributes[];
  }): {
    supervisors: PersonAttributes[];
    teamMembers: PersonAttributes[];
    dispatchers: PersonAttributes[];
    guests: PersonAttributes[];
  } {
    const supervisorIds = new Set(input.supervisors.map((p) => p.id));
    const teamMemberIds = new Set(input.teamMembers.map((p) => p.id));

    // 1. Dispatchers: keep only non-USDA. USDA dispatchers move to Team
    //    Members unless they're already a Supervisor (stay supervisor only).
    const dispatchers: PersonAttributes[] = [];
    const promotedToTeamMember: PersonAttributes[] = [];
    for (const d of input.dispatchers) {
      if (!this.isUsdaPerson(d)) {
        dispatchers.push(d);
        continue;
      }
      if (supervisorIds.has(d.id)) continue;
      if (!teamMemberIds.has(d.id)) promotedToTeamMember.push(d);
    }
    const dispatcherIds = new Set(dispatchers.map((p) => p.id));

    // 2. Team Members: append the promoted-from-Dispatcher USDA users.
    const teamMembersStaged = [...input.teamMembers, ...promotedToTeamMember];

    // 3. Filter Team Members down to USDA. Non-USDA team members become
    //    Guests — but only when they aren't already (post-migration)
    //    Dispatchers, since a non-USDA person can legitimately be a
    //    Dispatcher and shouldn't show up in two sections.
    const teamMembers: PersonAttributes[] = [];
    const demotedToGuest: PersonAttributes[] = [];
    for (const tm of teamMembersStaged) {
      if (this.isUsdaPerson(tm)) {
        teamMembers.push(tm);
        continue;
      }
      if (dispatcherIds.has(tm.id)) continue;
      demotedToGuest.push(tm);
    }

    // 4. Guests: start with team-scoped guests, append demoted team
    //    members, then de-dupe by id.
    const guestsCombined = [...input.guests, ...demotedToGuest];
    const seen = new Set<string>();
    const guests: PersonAttributes[] = [];
    for (const g of guestsCombined) {
      if (seen.has(g.id)) continue;
      seen.add(g.id);
      guests.push(g);
    }

    return {
      supervisors: input.supervisors,
      teamMembers,
      dispatchers,
      guests,
    };
  }

  getParentGroupDropdownOptions() {
    const adminGroups$ =
      this.rolePermissionService.searchMyUserGroupsByPermission({
        permissionIds: ['create_child_usergroup'],
      });
    const ShowUserGroups$ = combineLatest([
      adminGroups$,
      this.userGroupService.allRegions$,
      this.userGroupService.allForestLabs$,
    ]).pipe(
      map(([adminGroups, allRegions, allForests]) => {
        const showItems: { [id: string]: true } = {};
        adminGroups.forEach((group) => {
          if (group.tierLabel === 'Region') {
            showItems[group.id] = true;
            group.children.forEach((child) => (showItems[child.id] = true));
          } else if (group.tierLabel === 'ForestLab') {
            showItems[group.id] = true;
            showItems[group.parentId] = true;
          }
        });
        const showRegions = allRegions.filter((group) => showItems[group.id]);
        const showForests = allForests.filter((group) => showItems[group.id]);
        return { showRegions, showForests };
      }),
      shareReplay(1)
    );

    const op_mapToOptions = map((groups: UserGroupAttributes[]) =>
      groups.map((group) => ({
        label: group.labelPrefix + group.label,
        value: group.id,
      }))
    );

    const regionOptions$ = ShowUserGroups$.pipe(
      map((val) => val.showRegions),
      op_mapToOptions
    );
    const regionStationId$ =
      this.userGroupControls.regionStation.valueChanges.pipe(
        startWith(this.userGroupControls.regionStation.value)
      );
    const forestOptions$ = combineLatest([
      regionStationId$,
      ShowUserGroups$,
    ]).pipe(
      map(([rsGroupId, { showForests }]) =>
        showForests.filter((forest) => forest.parentId === rsGroupId)
      ),
      op_mapToOptions
    );
    return { regionOptions$, forestOptions$ };
  }

  removePersonFromList(person: PersonAttributes, listControl: FormControl) {
    let arr = listControl.value as PersonAttributes[];
    arr = arr.filter((item) => item.id !== person.id);
    listControl.setValue(arr);
  }

  /**
   * v2.4: a person may hold only one role on a team. Returns the label of the
   * role they already occupy in one of the OTHER sections (so the add can be
   * rejected with a clear message), or null when they're free to add to the
   * target section. The person must be removed from their current role before
   * being added to a different one.
   */
  private conflictingRoleLabel(
    personId: string,
    targetList: FormControl<PersonAttributes[]>
  ): string | null {
    const sections: {
      control: FormControl<PersonAttributes[]>;
      label: string;
    }[] = [
      { control: this.membersControls.listSupervisor, label: 'Supervisor' },
      { control: this.membersControls.listTeamMember, label: 'Team Member' },
      { control: this.membersControls.listDispatcher, label: 'Dispatcher' },
      { control: this.membersControls.listGuest, label: 'Guest' },
    ];
    for (const section of sections) {
      if (section.control === targetList) continue;
      if ((section.control.value ?? []).some((p) => p.id === personId)) {
        return section.label;
      }
    }
    return null;
  }

  GetRoleDifferences(userGroupId: uuid) {
    let modifiedRoles = [];
    if (this.canEditSupervisors) {
      let setSupervisorsTo: PersonAttributes[] =
        this.membersControls.listSupervisor.value;
      setSupervisorsTo.forEach((member) => {
        if (
          !this.initialSupervisors.find((initial) => member.id === initial.id)
        ) {
          modifiedRoles.push({
            action: 'Add',
            PersonId: member.id,
            RoleId: 'supervisor',
            UserGroupId: userGroupId,
          });
        }
      });
      this.initialSupervisors.forEach((member) => {
        if (!setSupervisorsTo.find((initial) => member.id === initial.id)) {
          modifiedRoles.push({
            action: 'Remove',
            PersonId: member.id,
            RoleId: 'supervisor',
            UserGroupId: userGroupId,
          });
        }
      });
    }
    if (this.canEditTeamMembers) {
      let setTeamMembersTo: PersonAttributes[] =
        this.membersControls.listTeamMember.value;
      setTeamMembersTo.forEach((member) => {
        if (
          !this.initialTeamMembers.find((initial) => member.id === initial.id)
        ) {
          modifiedRoles.push({
            action: 'Add',
            PersonId: member.id,
            RoleId: 'team_member',
            UserGroupId: userGroupId,
          });
        }
      });
      this.initialTeamMembers.forEach((member) => {
        if (!setTeamMembersTo.find((initial) => member.id === initial.id)) {
          modifiedRoles.push({
            action: 'Remove',
            PersonId: member.id,
            RoleId: 'team_member',
            UserGroupId: userGroupId,
          });
        }
      });
    }
    if (this.canEditDispatchers) {
      let setDispatchersTo: PersonAttributes[] =
        this.membersControls.listDispatcher.value;
      setDispatchersTo.forEach((member) => {
        if (
          !this.initialDispatchers.find((initial) => member.id === initial.id)
        ) {
          modifiedRoles.push({
            action: 'Add',
            PersonId: member.id,
            RoleId: 'dispatcher',
            UserGroupId: userGroupId,
          });
        }
      });
      this.initialDispatchers.forEach((member) => {
        if (!setDispatchersTo.find((initial) => member.id === initial.id)) {
          modifiedRoles.push({
            action: 'Remove',
            PersonId: member.id,
            RoleId: 'dispatcher',
            UserGroupId: userGroupId,
          });
        }
      });
    }
    if (this.canEditGuests) {
      let setGuestsTo: PersonAttributes[] =
        this.membersControls.listGuest.value;
      setGuestsTo.forEach((member) => {
        if (
          !this.initialGuests.find((initial) => member.id === initial.id)
        ) {
          modifiedRoles.push({
            action: 'Add',
            PersonId: member.id,
            RoleId: 'guest',
            UserGroupId: userGroupId,
          });
        }
      });
      this.initialGuests.forEach((member) => {
        if (!setGuestsTo.find((initial) => member.id === initial.id)) {
          modifiedRoles.push({
            action: 'Remove',
            PersonId: member.id,
            RoleId: 'guest',
            UserGroupId: userGroupId,
          });
        }
      });
    }

    return modifiedRoles;
  }

  saveFormData() {
    let comp = this;
    let data = comp.userGroupFormGroup.value as Required<FormData>;
    let userGroupAttributes = this.convertDataToAttr(data);
    let saveUserGroup$: Observable<UserGroupAttributes>;
    let userGroupId$: Observable<string>;

    if (this.canEditUserGroup) {
      saveUserGroup$ = this.userGroupService.saveUserGroup(userGroupAttributes);
    } else {
      saveUserGroup$ = of(userGroupAttributes);
    }
    userGroupId$ = saveUserGroup$.pipe(
      take(1),
      map((userGroup) => userGroup.id),
      switchMap((id) => {
        //GetRoleDifferences includes limitations for if the user can/cannot edit.
        let differences = this.GetRoleDifferences(id);
        if (differences.length === 0) {
          return of(id);
        }
        let operations = differences.map((change) =>
          change.action === 'Add'
            ? this.rolePermissionService.assignRole$(change)
            : this.rolePermissionService.unassignRole$(change)
        );
        let operationBulk = combineLatest(operations).pipe(map((_) => id));
        return operationBulk;
      }),
      tap(() => this.userGroupService._refreshList$.next()),
      tap((id) => {
        this.refreshUserGroup(id);
      }),
      catchError((err, caught) => {
        this.refreshUserGroup(this._userGroupId);
        throw err;
      })
    );
    return userGroupId$;
  }

  deleteData() {
    if (this.userGroupFormGroup.dirty) {
      throw new Error(
        'There are unsaved changes. Either refresh the data, or save.'
      );
    }
    // Delete user group
    let userGroupDel$ = this.userGroupService.deleteUserGroup(this._userGroupId);
    return userGroupDel$;
  }
  convertDataToAttr(data: FormData): UserGroupAttributes {
    if (data.forestLab) {
      data.tier = 3;
      data.parentId = data.forestLab;
    } else {
      data.tier = 2;
      data.parentId = data.regionStation;
    }

    let userGroup = {
      id: data.id || undefined,
      label: data.label,
      tier: data.tier,
      tierLabel: data.tierLabel || 'Team',
      description: data.description,
      pointOfContact: data.pointOfContact,
      contactEmail: data.contactEmail,
      parentId: data.parentId || null,
      parent: data.parent || null,
    };
    return userGroup;
  }

  convertObjectsToData(userGroup?: UserGroupAttributes): FormData {
    let regionStation = null;
    let forestLab = null;
    if (userGroup.tier == 3) {
      regionStation = userGroup.parent?.parentId;
      forestLab = userGroup.parentId;

      // set view only variables
      this.regionStation = userGroup.parent?.parent;
      this.forestLab = userGroup.parent;
    } else {
      regionStation = userGroup.parentId;
      forestLab = null;

      // set view only variabled
      this.regionStation = userGroup.parent;
      this.forestLab = null;
    }

    return {
      id: userGroup?.id || undefined,
      label: userGroup?.label || '',
      tier: userGroup?.tier ?? undefined,
      tierLabel: userGroup?.tierLabel || 'Team', //same as tier value, this will have to be changed, but for now all new user groups are "teams"
      description: userGroup?.description || '',
      pointOfContact: userGroup?.pointOfContact || '',
      contactEmail: userGroup?.contactEmail || '',
      parentId: userGroup?.parentId || null,
      regionStation: regionStation,
      forestLab: forestLab,
      parent: userGroup?.parent || null,
    };
  }
}
type FormData = Required<UserGroupFormComponent['userGroupFormGroup']['value']>;
